import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';

type Platform = 'android' | 'ios';
type MobileVersion = {
  id: string;
  plataforma: Platform;
  version_nombre: string;
  build_numero: number;
  tienda_url: string | null;
  mensaje: string;
  aprobada_tienda: boolean;
  activa: boolean;
  obligatoria: boolean;
  activada_at: string | null;
  created_at: string;
};

const platformData: Record<Platform, { label: string; icon: string; storeUrl: string }> = {
  android: {
    label: 'Android',
    icon: 'ri-android-line',
    storeUrl: 'https://play.google.com/store/apps/details?id=cl.maximumstudio',
  },
  ios: {
    label: 'iPhone',
    icon: 'ri-apple-line',
    storeUrl: 'https://apps.apple.com/cl/app/maximumstudio/id6762581668',
  },
};

const emptyForm = (platform: Platform, nextBuild: number) => ({
  plataforma: platform,
  version_nombre: '',
  build_numero: String(nextBuild),
  tienda_url: platformData[platform].storeUrl,
  mensaje: 'Existe una nueva versión disponible. Actualiza para continuar.',
  aprobada_tienda: false,
});

export default function MobileVersionsSettings({ onClose }: { onClose: () => void }) {
  const [versions, setVersions] = useState<MobileVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => emptyForm('android', 29));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await supabase
      .from('app_versiones_moviles')
      .select('*')
      .order('build_numero', { ascending: false });
    if (loadError) setError(loadError.message);
    setVersions((data || []) as MobileVersion[]);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const maxBuild = useMemo(() => ({
    android: Math.max(0, ...versions.filter((v) => v.plataforma === 'android').map((v) => v.build_numero)),
    ios: Math.max(0, ...versions.filter((v) => v.plataforma === 'ios').map((v) => v.build_numero)),
  }), [versions]);

  const openForm = (platform: Platform) => {
    setForm(emptyForm(platform, maxBuild[platform] + 1));
    setShowForm(true);
    setError(null);
  };

  const createVersion = async () => {
    const build = Number(form.build_numero);
    if (!/^\d+\.\d+\.\d+$/.test(form.version_nombre.trim())) {
      setError('La versión debe usar el formato 3.1.0.');
      return;
    }
    if (!Number.isInteger(build) || build <= maxBuild[form.plataforma]) {
      setError(`El build debe ser mayor que ${maxBuild[form.plataforma]}.`);
      return;
    }
    if (!form.tienda_url.startsWith('https://')) {
      setError('Ingresa una URL https válida de la tienda.');
      return;
    }
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase.from('app_versiones_moviles').insert({
      plataforma: form.plataforma,
      version_nombre: form.version_nombre.trim(),
      build_numero: build,
      tienda_url: form.tienda_url.trim(),
      mensaje: form.mensaje.trim(),
      aprobada_tienda: form.aprobada_tienda,
    });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    setShowForm(false);
    await load();
  };

  const setApproved = async (version: MobileVersion, approved: boolean) => {
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('app_versiones_moviles')
      .update({ aprobada_tienda: approved })
      .eq('id', version.id);
    setSaving(false);
    if (updateError) setError(updateError.message);
    else await load();
  };

  const activate = async (version: MobileVersion) => {
    setSaving(true);
    setError(null);
    const { error: activateError } = await supabase.rpc('activar_version_movil', {
      p_version_id: version.id,
      p_obligatoria: true,
    });
    setSaving(false);
    if (activateError) setError(activateError.message);
    else await load();
  };

  const setMandatory = async (platform: Platform, mandatory: boolean) => {
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase.rpc('configurar_obligatoriedad_version_movil', {
      p_plataforma: platform,
      p_obligatoria: mandatory,
    });
    setSaving(false);
    if (updateError) setError(updateError.message);
    else await load();
  };

  return (
    <div className="flex max-h-[90vh] flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
        <div>
          <h3 className="text-xl font-bold text-gray-950">Versiones de la aplicación móvil</h3>
          <p className="mt-1 text-sm text-gray-500">Activa una versión solamente después de que la tienda la apruebe.</p>
        </div>
        <button onClick={onClose} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-gray-100" aria-label="Cerrar">
          <i className="ri-close-line text-2xl text-gray-500" />
        </button>
      </div>

      <div className="overflow-y-auto p-6">
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-bold"><i className="ri-lock-line mr-2" />Protección contra retrocesos</p>
          <p className="mt-1">Una vez activado un build, el sistema nunca permitirá activar uno menor, aunque la actualización obligatoria se deshabilite.</p>
        </div>
        {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

        {loading ? <div className="py-16 text-center"><i className="ri-loader-4-line animate-spin text-3xl text-teal-700" /></div> : (
          <div className="grid gap-5 lg:grid-cols-2">
            {(['android', 'ios'] as Platform[]).map((platform) => {
              const rows = versions.filter((v) => v.plataforma === platform);
              const active = rows.find((v) => v.activa);
              return (
                <section key={platform} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <header className="flex items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center gap-3">
                      <i className={`${platformData[platform].icon} text-3xl text-gray-800`} />
                      <div><h4 className="font-bold text-gray-950">{platformData[platform].label}</h4><p className="text-xs text-gray-500">Mayor build registrado: {maxBuild[platform] || 'ninguno'}</p></div>
                    </div>
                    <button onClick={() => openForm(platform)} className="rounded-lg bg-teal-800 px-3 py-2 text-sm font-bold text-white hover:bg-teal-900"><i className="ri-add-line mr-1" />Nueva</button>
                  </header>
                  {active && (
                    <div className="m-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div><p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Versión activa</p><p className="text-xl font-extrabold text-gray-950">{active.version_nombre} <span className="text-sm font-semibold text-gray-500">build {active.build_numero}</span></p></div>
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${active.obligatoria ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>{active.obligatoria ? 'Obligatoria' : 'Sin bloqueo'}</span>
                      </div>
                      <button disabled={saving} onClick={() => void setMandatory(platform, !active.obligatoria)} className="mt-3 w-full rounded-lg border border-emerald-700 px-3 py-2 text-sm font-bold text-emerald-900 disabled:opacity-50">
                        {active.obligatoria ? 'Desactivar actualización obligatoria' : 'Exigir actualización'}
                      </button>
                    </div>
                  )}
                  <div className="divide-y divide-gray-100">
                    {rows.map((version) => (
                      <div key={version.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div><p className="font-bold text-gray-900">{version.version_nombre} · build {version.build_numero}</p><p className="mt-1 text-xs text-gray-500">{new Date(version.created_at).toLocaleString('es-CL')}</p></div>
                          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700"><input type="checkbox" checked={version.aprobada_tienda} disabled={saving || version.activa} onChange={(e) => void setApproved(version, e.target.checked)} />Aprobada</label>
                        </div>
                        {!version.activa && <button disabled={saving || !version.aprobada_tienda || version.build_numero < maxBuild[platform]} onClick={() => void activate(version)} className="mt-3 rounded-lg bg-gray-900 px-3 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">Activar y exigir actualización</button>}
                      </div>
                    ))}
                    {rows.length === 0 && <p className="p-6 text-center text-sm text-gray-500">Sin versiones registradas.</p>}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="absolute inset-0 z-10 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between"><h4 className="text-lg font-bold">Nueva versión para {platformData[form.plataforma].label}</h4><button onClick={() => setShowForm(false)}><i className="ri-close-line text-2xl" /></button></div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-gray-700">Versión<input value={form.version_nombre} onChange={(e) => setForm({ ...form, version_nombre: e.target.value })} placeholder="3.1.0" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5" /></label>
              <label className="text-sm font-semibold text-gray-700">Build<input type="number" min={maxBuild[form.plataforma] + 1} value={form.build_numero} onChange={(e) => setForm({ ...form, build_numero: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5" /></label>
            </div>
            <label className="mt-4 block text-sm font-semibold text-gray-700">URL de la tienda<input value={form.tienda_url} onChange={(e) => setForm({ ...form, tienda_url: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5" /></label>
            <label className="mt-4 block text-sm font-semibold text-gray-700">Mensaje<textarea value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5" /></label>
            <label className="mt-4 flex items-center gap-3 rounded-lg bg-gray-50 p-3 text-sm font-semibold text-gray-800"><input type="checkbox" checked={form.aprobada_tienda} onChange={(e) => setForm({ ...form, aprobada_tienda: e.target.checked })} />La tienda ya aprobó esta versión</label>
            <div className="mt-6 flex justify-end gap-3"><button onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 font-semibold">Cancelar</button><button disabled={saving} onClick={() => void createVersion()} className="rounded-lg bg-teal-800 px-4 py-2.5 font-bold text-white disabled:opacity-50">Guardar versión</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
